import { proxy } from "valtio"
import { deepClone } from "valtio/utils"
import type { FlowStateVariants } from "../flow/flow-state"

// -- Helpers -------------------------------------------------------
const DEFAULT_SUCCESS_DELAY = 1000

// -- Types ---------------------------------------------------------
type FlowCallbacksOptions = {
  onSuccessDelay?: number
}

export type FlowCallbacks = {
  onStart?: (flow: FlowStateVariants) => void
  onSuccess?: (flow: FlowStateVariants) => void
  onError?: (flow: FlowStateVariants, errorData?: unknown) => void
  onEnd?: (flow: FlowStateVariants) => void
  options?: FlowCallbacksOptions
}

type FlowCallbackStateType = FlowCallbacks[]

// -- State ---------------------------------------------------------
const INITIAL_STATE: FlowCallbackStateType = []

export const FlowCallbackState = proxy<FlowCallbackStateType>(
  deepClone(INITIAL_STATE),
)

// -- Actions -------------------------------------------------------
export const FlowCallbackActions = {
  addCallbacks(callbacks: FlowCallbacks) {
    callbacks.options = {
      onSuccessDelay:
        callbacks.options?.onSuccessDelay ?? DEFAULT_SUCCESS_DELAY,
    }

    FlowCallbackState.push(callbacks)

    return () => {
      const index = FlowCallbackState.findIndex(
        cb =>
          cb.onEnd === callbacks.onEnd &&
          cb.onSuccess === callbacks.onSuccess &&
          cb.onError === callbacks.onError &&
          cb.onStart === callbacks.onStart,
      )
      if (index > -1) {
        FlowCallbackState.splice(index, 1)
      }
    }
  },

  startAll(flow: FlowStateVariants) {
    FlowCallbackState.forEach(cb => {
      cb.onStart?.(flow)
    })
  },

  endAll(flow: FlowStateVariants) {
    FlowCallbackState.forEach(cb => {
      cb.onEnd?.(flow)
    })
  },

  succeedAll(flow: FlowStateVariants) {
    FlowCallbackState.forEach(cb => {
      if (cb.options?.onSuccessDelay) {
        setTimeout(() => {
          cb.onSuccess?.(flow)
        }, cb.options.onSuccessDelay)
      } else {
        cb.onSuccess?.(flow)
      }
    })
  },

  failAll(flow: FlowStateVariants, errorData?: unknown) {
    FlowCallbackState.forEach(cb => {
      cb.onError?.(flow, errorData)
    })
  },
}
